import { useMemo } from 'react';
import { encodeQrMatrix, type EcLevel } from '@/lib/qrcode';

export interface QrCodeSvgProps {
  value: string;
  size?: number;
  ecLevel?: EcLevel;
  /** Quiet zone modules around the matrix (spec recommends 4). */
  margin?: number;
  className?: string;
  /** Module colour. Defaults to black. */
  fg?: string;
  /** Background colour. Defaults to white. */
  bg?: string;
}

export function QrCodeSvg({
  value,
  size = 240,
  ecLevel = 'M',
  margin = 4,
  className,
  fg = '#0f172a',
  bg = '#ffffff',
}: QrCodeSvgProps) {
  const { size: n, modules } = useMemo(
    () => encodeQrMatrix(value, ecLevel),
    [value, ecLevel],
  );

  // Combine same-row dark runs into single rect elements to keep the DOM small.
  const path = useMemo(() => {
    const parts: string[] = [];
    for (let r = 0; r < n; r++) {
      const row = modules[r] ?? [];
      let c = 0;
      while (c < n) {
        if (!row[c]) {
          c++;
          continue;
        }
        let runEnd = c;
        while (runEnd < n && row[runEnd]) runEnd++;
        // Move to (c, r), draw horizontal line of (runEnd - c) modules,
        // then close a 1-tall rectangle.
        parts.push(`M${c + margin} ${r + margin}h${runEnd - c}v1h-${runEnd - c}z`);
        c = runEnd;
      }
    }
    return parts.join('');
  }, [modules, n, margin]);

  const viewBox = n + margin * 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${viewBox} ${viewBox}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`QR code for ${value}`}
      className={className}
      style={{ background: bg }}
    >
      <path d={path} fill={fg} />
    </svg>
  );
}
